import {Box, Center, Grid, Heading, HStack, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';

import {buildArray, useNavigate} from 'shared/web';

import {Button} from '../../components/Button/Button';
import {CardOo} from '../../components/CardOo/CardOo';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {useViewedAlternative} from '../../hooks/useViewedAlternative';
import {ThemeColorPath} from '../../theme/theme';
import {
  buildComparisonRows,
  countDifferingRows,
  filterRows,
  getDefaultReferenceId,
  hasTotal,
  sortByLetter,
} from '../../utils/buildComparisonRows';
import {getAlternativeChoiceStatus} from '../../utils/getAlternativeChoiceStatus';
import {getAlternativeLabel} from '../../utils/getAlternativeLabel';
import {translateWithFallback as t} from '../../utils/translateWithFallback';
import {ChooseAlternativeDialog} from './components/ChooseAlternativeDialog';
import {ComparisonTable} from './components/ComparisonTable';
import {ComparisonToolbar} from './components/ComparisonToolbar';
import {useChooseAlternative} from './hooks/useChooseAlternative';

type Notice = {tone: 'success' | 'warning' | 'info'; title: string; message?: string};

const NOTICE_BACKGROUND: Record<Notice['tone'], ThemeColorPath> = {
  success: 'semantic.bg.green',
  warning: 'severity.warningCard',
  info: 'accent.lightest',
};

/**
 * Customer comparison of every offer alternative, with the non-binding choice. Also the whole
 * page body for an offer that has alternatives but no deal vehicle yet.
 */
export function AlternativesComparison() {
  const {offerAlternatives, vehicle} = useGetOnlineOfferData();
  const params = useParams<{offerId: string}>();
  const location = useLocation();
  const navigate = useNavigate();
  const choice = useChooseAlternative();
  const {viewAlternative} = useViewedAlternative();
  const [pickedReferenceId, setPickedReferenceId] = useState<string | null>(null);
  const [isOnlyDifferences, setIsOnlyDifferences] = useState(false);

  if (isNil(offerAlternatives)) {
    return null;
  }

  const alternatives = sortByLetter(offerAlternatives.alternatives);
  // A picked reference that was withdrawn meanwhile falls back to the default.
  const reference =
    alternatives.find(
      (alternative) => alternative.id === pickedReferenceId && hasTotal(alternative)
    ) ??
    alternatives.find(
      (alternative) =>
        alternative.id === getDefaultReferenceId(alternatives, offerAlternatives.customerChoice)
    ) ??
    alternatives[0];
  const allRows = buildComparisonRows(alternatives);
  const rows = filterRows(allRows, isOnlyDifferences);
  const {customerChoice} = offerAlternatives;
  const chosenAlternative = alternatives.find(
    (alternative) => alternative.id === customerChoice?.alternativeId
  );

  const notices = buildArray<Notice>()
    .when(choice.failure === 'CHANGED' || (isNotNil(customerChoice) && !customerChoice.isCurrent), {
      tone: 'warning',
      title: t(
        'alternatives.notice.changed',
        'The offer has changed. Please confirm your choice again.'
      ),
    })
    .when(offerAlternatives.cannotChooseReason === 'IN_PROCESSING', {
      tone: 'info',
      title: t(
        'alternatives.notice.inProcessing',
        'Your adviser is already processing your selection. Contact them to request a change.'
      ),
    })
    .when(choice.failure === 'NOT_SELECTABLE', {
      tone: 'warning',
      title: t(
        'alternatives.notice.notSelectable',
        'This alternative can no longer be selected. Please choose another one.'
      ),
    })
    .when(choice.failure === 'FAILED', {
      tone: 'warning',
      title: t(
        'alternatives.notice.failed',
        'Your selection could not be saved. Please try again.'
      ),
    })
    .whenNotNil(
      customerChoice?.isCurrent ? chosenAlternative : null,
      (alternative): Notice =>
        getAlternativeChoiceStatus(offerAlternatives, alternative) === 'PREPARING'
          ? {
              tone: 'success',
              title: t('alternatives.notice.preparing', 'Your adviser is preparing {label}', {
                label: getAlternativeLabel(alternative),
              }),
              message: t(
                'alternatives.notice.preparingMessage',
                'Your adviser will confirm the next steps with you.'
              ),
            }
          : {
              tone: 'success',
              title: t('alternatives.notice.selected', '{label} selected', {
                label: getAlternativeLabel(alternative),
              }),
              message: t(
                'alternatives.notice.selectedMessage',
                'Your adviser will confirm the next steps. Your selection does not create a reservation, order or contract.'
              ),
            }
    );

  return (
    <Center justify="center" width="100%">
      {/* Fixed width, not flex-grow: the wide table must scroll inside, not stretch the page. */}
      <Box width="100%" maxWidth={432} paddingVertical={8} paddingHorizontal={[3, 8, 8, 20]}>
        <VStack spacing={6}>
          <Show when={isNotNil(vehicle)}>
            <HStack>
              <Button
                variant="link"
                leftIcon="arrowLeft"
                title={t('alternatives.comparison.back', 'Back to the offer')}
                onClick={() => navigate({pathname: `/${params.offerId}`, search: location.search})}
                data-testid="alternativesComparison-back"
              />
            </HStack>
          </Show>
          <VStack spacing={2}>
            <Heading size={1}>
              {t('alternatives.comparison.title', 'Compare your alternatives')}
            </Heading>
            <Text color="secondary">
              {t(
                'alternatives.comparison.subtitle',
                'See what changes, what is included and how much more you pay.'
              )}
            </Text>
          </VStack>

          {notices.map((notice) => (
            <CardOo key={notice.title} backgroundColor={NOTICE_BACKGROUND[notice.tone]} padding={4}>
              <VStack spacing={1}>
                <Text size="small" alternative data-testid="alternativesNotice">
                  {notice.title}
                </Text>
                <Show when={isNotNil(notice.message)}>
                  <Text size="small" color="secondary">
                    {notice.message}
                  </Text>
                </Show>
              </VStack>
            </CardOo>
          ))}

          <ComparisonToolbar
            alternatives={alternatives.filter(hasTotal)}
            referenceId={reference.id}
            onReferenceChange={setPickedReferenceId}
            isOnlyDifferences={isOnlyDifferences}
            onOnlyDifferencesChange={setIsOnlyDifferences}
            differencesCount={countDifferingRows(allRows)}
          />

          <Show onMobile>
            <Text size="xSmall" color="secondary">
              {t(
                'alternatives.comparison.swipeHint',
                'Swipe the table to compare the other vehicles →'
              )}
            </Text>
          </Show>

          <ComparisonTable
            alternatives={alternatives}
            rows={rows}
            reference={reference}
            offerAlternatives={offerAlternatives}
            onSelect={choice.request}
            onView={isNotNil(vehicle) ? viewAlternative : undefined}
          />

          <Grid columns={[1, 3]} spacing={6}>
            <Text size="xSmall" color="secondary">
              {t(
                'alternatives.comparison.notes.prices',
                'Package and option prices are already included in the vehicle price. Do not add them to the total again.'
              )}
            </Text>
            <Text size="xSmall" color="secondary">
              {t(
                'alternatives.comparison.notes.notListed',
                '“Not listed” means the equipment is not documented in this offered specification. It does not confirm that the equipment is absent.'
              )}
            </Text>
            <Text size="xSmall" color="secondary">
              {t(
                'alternatives.comparison.notes.delivery',
                'Delivery information is indicative unless your adviser explicitly confirms it.'
              )}
            </Text>
          </Grid>
        </VStack>
      </Box>

      <ChooseAlternativeDialog
        alternative={choice.pendingAlternative}
        isChoosing={choice.isChoosing}
        onConfirm={choice.confirm}
        onCancel={choice.cancel}
      />
    </Center>
  );
}
